import { useState } from 'react'

function AddressCard({ address, onDelete, onViewEmails, onManageKeys, apiKeyCount = 0 }) {
  const [copied, setCopied] = useState(null)

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    }
  }

  // Calculate expiration (90 days from creation)
  const getExpirationInfo = () => {
    if (!address.created_at) return null
    const created = new Date(address.created_at)
    const expires = new Date(created.getTime() + 90 * 24 * 60 * 60 * 1000)
    const now = new Date()
    const daysLeft = Math.ceil((expires - now) / (24 * 60 * 60 * 1000))

    if (daysLeft <= 0) {
      return { text: 'Expired', color: 'text-red-600' }
    } else if (daysLeft <= 7) {
      return { text: `Expires in ${daysLeft}d`, color: 'text-orange-600' }
    } else if (daysLeft <= 30) {
      return { text: `Expires in ${daysLeft}d`, color: 'text-yellow-600' }
    } else {
      return { text: `Expires in ${daysLeft}d`, color: 'text-gray-500' }
    }
  }

  const expiration = getExpirationInfo()

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Email</span>
              <button
                onClick={() => copyToClipboard(address.email, 'email')}
                className="text-gray-400 hover:text-gray-600"
                title="Copy email"
              >
                {copied === 'email' ? (
                  <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="mt-1 font-mono text-sm text-gray-900">{address.email}</p>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1.5" title="Emails received">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{address.email_count || 0}</span>
            </div>
            <div className="flex items-center gap-1.5" title="API keys">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>{apiKeyCount}</span>
            </div>
            {expiration && (
              <div className={`flex items-center gap-1.5 ${expiration.color}`} title="Time until expiration">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{expiration.text}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => onViewEmails(address)}
            className="rounded-md p-2 text-blue-600 hover:bg-blue-50"
            title="View Emails"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={() => onManageKeys(address)}
            className="rounded-md p-2 text-purple-600 hover:bg-purple-50"
            title="Manage API Keys"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(address)}
            className="rounded-md p-2 text-red-600 hover:bg-red-50"
            title="Delete Address"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddressCard
